import React, { useEffect } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { FullCalendarEvent } from '../hooks/useReservableCalendarView';
import {
  formatMonthEventLabel,
  MONTH_EVENT_TRANSLATION_KEYS,
} from './mapReservablesToMonthEvents';
import styles from './DayModal.scss';

type Props = {
  date: Date;
  events: FullCalendarEvent[];
  locale: string;
  anchorRect: DOMRect;
  onClose: () => void;
  onSelectEvent: (event: FullCalendarEvent) => void;
  onSelectGroupedEvent: (event: FullCalendarEvent) => void;
};

export function DayModal({
  date,
  events,
  locale,
  anchorRect,
  onClose,
  onSelectEvent,
  onSelectGroupedEvent,
}: Props) {
  const { t } = useTranslation();

  const { x, y, refs, strategy, context } = useFloating({
    open: true,
    onOpenChange: open => {
      if (!open) {
        onClose();
      }
    },
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const { getFloatingProps } = useInteractions([
    useDismiss(context),
    useRole(context),
  ]);

  useEffect(() => {
    refs.setPositionReference({
      getBoundingClientRect: () => anchorRect,
    });
  }, [refs, anchorRect]);

  const title = DateTime.fromJSDate(date)
    .setLocale(locale)
    .toLocaleString(DateTime.DATE_FULL);

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} initialFocus={-1}>
        <div
          ref={refs.setFloating}
          className={styles.popover}
          data-test="day-modal"
          style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          {...getFloatingProps()}
        >
          <div className={styles.header} data-test="day-modal-title">
            {title}
          </div>
          <div className={styles.divider} />
          <ul className={styles.list}>
            {events.map((event, index) => {
              const isGrouped = event.extendedProps.count > 1;
              const detail = isGrouped
                ? t(MONTH_EVENT_TRANSLATION_KEYS.multipleEntriesTitle, {
                    bookingCount: event.extendedProps.count,
                  })
                : event.extendedProps.username;

              const label = formatMonthEventLabel({
                start: event.start,
                reservableName: event.extendedProps.reservableName ?? '',
                detail,
                timeZone: event.extendedProps.timeZone,
                locale,
                variant: 'full',
              });

              return (
                <li
                  key={`${
                    event.extendedProps.interactionId ?? 'grouped'
                  }-${index}`}
                >
                  <button
                    type="button"
                    className={styles.row}
                    data-test="day-modal-row"
                    onClick={() =>
                      isGrouped
                        ? onSelectGroupedEvent(event)
                        : onSelectEvent(event)
                    }
                  >
                    <span
                      className={styles.statusDot}
                      data-test="day-modal-status"
                      style={{
                        backgroundColor: event.backgroundColor,
                        borderColor: event.borderColor,
                      }}
                    />
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}
